import { ControlCodes } from '@bcube/shared/constants';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Loader2, Plus, Trash2, Upload } from 'lucide-react';
import { useMemo, useState } from 'react';
import { type AccountUpload, accountUploadApi } from '@/api/upload/account-uploads';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileUpload } from '@/components/ui/file-upload';
import { Input } from '@/components/ui/input';
import { TableSkeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuth } from '@/context/auth-context';
import { useToast } from '@/hooks/use-toast';
import { downloadFromUrl } from '@/lib/utils';

interface AccountAttachmentsTabProps {
  accountId: string;
}

const ACCEPTED_TYPES = '.pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.csv';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
}

/** Get file extension from a filename */
function getFileExtension(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  return lastDot !== -1 ? filename.substring(lastDot) : '';
}

/** Get file name without extension */
function getFileBaseName(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  return lastDot !== -1 ? filename.substring(0, lastDot) : filename;
}

/** Extract the original filename from the S3 key (format: {accountId}/{uuid}-{fileName}) */
function extractFileName(key: string): string {
  const afterSlash = key.includes('/') ? key.split('/').pop()! : key;
  // UUID is 36 chars + 1 hyphen separator = skip first 37 chars
  if (afterSlash.length > 37) {
    return afterSlash.substring(37);
  }
  return afterSlash;
}

export function AccountAttachmentsTab({ accountId }: AccountAttachmentsTabProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { restrictFeatureByProfile } = useAuth();

  const canDelete = restrictFeatureByProfile(
    ControlCodes.commercial_accounts_access_tab_attachments_delete,
  );

  const [filterName, setFilterName] = useState('');
  const [appliedFilter, setAppliedFilter] = useState('');
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [customFileNames, setCustomFileNames] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AccountUpload | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['account-uploads', accountId, 'attachment'],
    queryFn: () => accountUploadApi.list(accountId, 'attachment'),
    enabled: !!accountId,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => accountUploadApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-uploads', accountId, 'attachment'] });
      toast({ title: 'Sucesso', description: 'Anexo removido.' });
    },
    onError: () => {
      toast({ title: 'Erro', description: 'Falha ao remover anexo.', variant: 'destructive' });
    },
  });

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setIsUploading(true);
    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        if (file.size > MAX_FILE_SIZE) {
          toast({
            title: 'Erro',
            description: `Arquivo "${file.name}" excede o limite de 10MB.`,
            variant: 'destructive',
          });
          continue;
        }

        const ext = getFileExtension(file.name);
        const customName = customFileNames[i]?.trim();
        const displayName = customName ? `${customName}${ext}` : file.name;

        const { uploadUrl, key, id } = await accountUploadApi.prepareUpload(
          accountId,
          displayName,
          displayName,
          file.type,
          'attachment',
        );

        await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });

        await accountUploadApi.confirmUpload({
          id,
          accountId,
          name: displayName,
          key,
          category: 'attachment',
          extension: ext,
          contentType: file.type,
          sizeInBytes: file.size,
        });
      }

      queryClient.invalidateQueries({ queryKey: ['account-uploads', accountId, 'attachment'] });
      toast({ title: 'Sucesso', description: 'Anexo(s) enviado(s) com sucesso.' });
      setIsUploadDialogOpen(false);
      setSelectedFiles([]);
      setCustomFileNames([]);
    } catch {
      toast({ title: 'Erro', description: 'Falha ao enviar anexo.', variant: 'destructive' });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async (attachment: AccountUpload) => {
    try {
      const url = await accountUploadApi.getDownloadUrl(attachment.key);
      await downloadFromUrl(url, attachment.name || extractFileName(attachment.key));
    } catch {
      toast({
        title: 'Erro',
        description: 'Falha ao obter link de download.',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id);
    setDeleteTarget(null);
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setAppliedFilter(filterName);
  };

  const clearFilters = () => {
    setFilterName('');
    setAppliedFilter('');
  };

  const filtered = useMemo(() => {
    const docs = data?.docs ?? [];
    if (!appliedFilter.trim()) return docs;
    const term = appliedFilter.toLowerCase();
    return docs.filter(
      (a) =>
        a.name.toLowerCase().includes(term) ||
        extractFileName(a.key).toLowerCase().includes(term) ||
        (a.uploadedBy?.name ?? '').toLowerCase().includes(term),
    );
  }, [data?.docs, appliedFilter]);

  const columnCount = canDelete ? 5 : 4;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Anexos</h2>
          <p className="text-muted-foreground">Visualize e gerencie os anexos desta conta</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setIsUploadDialogOpen(true)}
            className="bg-black text-white hover:bg-gray-800"
          >
            <Plus className="h-4 w-4 mr-2" />
            Anexar
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros de busca</CardTitle>
          <CardDescription>
            Preencha os filtros abaixo e clique em &ldquo;Pesquisar&rdquo;
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Input
                  label="Nome"
                  placeholder="Filtrar por nome"
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={clearFilters}>
                Limpar
              </Button>
              <Button type="submit" className="bg-black text-white hover:bg-gray-800">
                Pesquisar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading ? (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <TableSkeleton rows={5} />
        </div>
      ) : (
        <div className="w-full space-y-4">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Usuário</TableHead>
                  <TableHead>Data de Criação</TableHead>
                  <TableHead>Nome do Arquivo</TableHead>
                  <TableHead className="w-25">Ação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columnCount} className="text-center py-8 text-gray-500">
                      Nenhum anexo encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((attachment) => (
                    <TableRow key={attachment.id}>
                      <TableCell>
                        <span className="font-medium">{attachment.name}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{attachment.uploadedBy?.name ?? '-'}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{formatDate(attachment.createdAt)}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{extractFileName(attachment.key)}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            title="Download"
                            variant="ghost"
                            className="rounded bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            onClick={() => handleDownload(attachment)}
                          >
                            <Download className="size-5" />
                          </Button>
                          {canDelete && (
                            <Button
                              title="Excluir"
                              variant="ghost"
                              className="rounded bg-white px-2 py-1 text-xs font-semibold text-red-600 shadow-sm ring-1 ring-inset ring-red-300 hover:bg-red-50"
                              onClick={() => setDeleteTarget(attachment)}
                            >
                              <Trash2 className="size-5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Anexar Arquivo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <FileUpload
              handleFile={(files) => {
                const newFiles = files || [];
                setSelectedFiles(newFiles);
                setCustomFileNames(newFiles.map((f) => getFileBaseName(f.name)));
              }}
              accept={ACCEPTED_TYPES}
              multiple
              label="Selecione os arquivos"
            />
            {selectedFiles.length > 0 && (
              <div className="space-y-2">
                {selectedFiles.map((file, index) => (
                  <div key={file.name}>
                    <label
                      htmlFor={`file-name-${index}`}
                      className="text-sm font-medium leading-none"
                    >
                      {selectedFiles.length > 1
                        ? `Nome do arquivo ${index + 1}`
                        : 'Nome do arquivo'}
                    </label>
                    <div className="mt-1.5 flex">
                      <Input
                        id={`file-name-${index}`}
                        value={customFileNames[index] ?? ''}
                        onChange={(e) =>
                          setCustomFileNames((prev) => {
                            const next = [...prev];
                            next[index] = e.target.value;
                            return next;
                          })
                        }
                        placeholder="Nome personalizado"
                        className="rounded-r-none border-r-0"
                      />
                      <span className="inline-flex items-center rounded-r-md border border-l-0 border-input bg-muted px-3 text-sm text-muted-foreground">
                        {getFileExtension(file.name)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Formatos aceitos: PDF, PNG, JPG, DOC, DOCX, XLS, XLSX, CSV. Máximo 10MB por arquivo.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setIsUploadDialogOpen(false);
                  setSelectedFiles([]);
                  setCustomFileNames([]);
                }}
                disabled={isUploading}
              >
                Cancelar
              </Button>
              <Button onClick={handleUpload} disabled={selectedFiles.length === 0 || isUploading}>
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-1" />
                    Enviar
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o anexo &ldquo;{deleteTarget?.name}&rdquo;? Esta ação
              não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
